//@flow
import {Map} from 'immutable';
import {useCallback, useState} from 'react';

type Record<K, V> = {
  [key: K]: V,
};

type InitialValue<K, V> = Record<K, V>;
export function useMap<K, V>(
  initialState?: InitialValue<K, V>
): {
  get: (key: K) => void | V,
  set: (key: K, value: V) => void,
  delete: (key: K) => void,
  clear: () => void,
  has: (key: K) => boolean,
  size: () => number,
  entries: () => Array<[K, V]>,
  keys: () => Array<K>,
  values: () => Array<V>,
} {
  const [innerState, setInnerState] = useState<Map<K, V>>((): Map<K, V> => {
    if (!initialState) return Map<K, V>();
    // NOTE: This is a bit of a hack to handle the number keys, because js always converts object keys to strings
    let map = Map<K, V>();

    Object.entries(initialState).forEach(([key, value]) => {
      const parsedKey = isNaN(Number(key)) ? key : Number(key);
      map = map.set(parsedKey, value);
    });

    return map;
  });

  const map = {
    get: (key: K): V | void => innerState.get(key),
    set: useCallback((key: K, value: V): void => {
      setInnerState(innerState => innerState.set(key, value));
    }, []),
    delete: useCallback((key: K): void => {
      setInnerState(innerState => innerState.delete(key));
    }, []),
    clear: useCallback((): void => {
      setInnerState(Map());
    }, []),
    has: (key: K): boolean => innerState.has(key),
    size: (): number => innerState.size,
    entries: (): Array<[K, V]> => Array.from(innerState.entries()),
    keys: (): Array<K> => Array.from(innerState.keys()),
    values: (): Array<V> => Array.from(innerState.values()),
  };

  return map;
}
